import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Swords, Crown, Star, Loader2, Trophy } from "lucide-react";
import { categoryLabel } from "@/lib/clubwars";
import { CwStandings } from "@/components/CwStandings";
import api from "@/lib/api";

export default function CwTvBoard() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);

  const load = useCallback(() => {
    api.get(`/public/cw/${token}`).then(({ data }) => { setD(data); setErr(false); })
      .catch((e) => { if (e.response?.status === 404) setErr(true); });
  }, [token]);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);

  if (err) return <Center>Board not found.</Center>;
  if (!d) return <Center><Loader2 className="w-7 h-7 animate-spin text-primary-c" /></Center>;

  const allLines = (d.fixtures || []).flatMap((fx) =>
    (fx.lines || []).filter((l) => l.status !== "dead").map((l, i) => ({ ...l, _fx: fx, _key: `${fx.fixture_id}-${l.type}-${l.entry_label || ""}-${i}` })));
  const live = allLines.filter((l) => l.status === "live");
  const finished = allLines.filter((l) => l.status === "final");
  const upcoming = allLines.filter((l) => l.status !== "live" && l.status !== "final");

  return (
    <div className="h-screen flex flex-col overflow-hidden grain" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <header className="shrink-0 flex items-center gap-3 px-5 sm:px-8 py-3 border-b" style={{ borderColor: "var(--border)" }}>
        <Swords className="w-7 h-7 text-primary-c" />
        <div className="min-w-0">
          <h1 className="font-display font-black text-2xl sm:text-3xl tracking-tight truncate">{d.name}</h1>
          {d.venue && <div className="text-xs truncate" style={{ color: "var(--text-2)" }}>{d.venue}</div>}
        </div>
        <span className="ml-auto text-[10px] mono uppercase tracking-widest rounded-full px-3 py-1 glass text-primary-c flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-primary-c animate-pulse" /> Live
        </span>
      </header>

      {d.completed ? (
        <div className="flex-1 min-h-0 flex items-center justify-center p-6">
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            className="rounded-3xl glow-border p-10 text-center max-w-lg w-full" style={{ background: "rgba(234,179,8,0.10)" }} data-testid="cw-recap">
            <Trophy className="w-16 h-16 mx-auto mb-4" style={{ color: "#eab308" }} />
            <div className="text-xs uppercase tracking-widest" style={{ color: "var(--text-2)" }}>Champion</div>
            <div className="font-display font-black text-4xl mt-1">{d.champion}</div>
            {d.mvp && <div className="mt-4 inline-flex items-center gap-2 glass rounded-full px-4 py-2 text-sm font-bold"><Star className="w-4 h-4" style={{ color: "#eab308" }} /> MVP: {d.mvp.name} · {d.mvp.line_wins} line wins</div>}
          </motion.div>
        </div>
      ) : (
        <div className="flex-1 min-h-0 grid grid-cols-3 gap-4 p-4">
          {d.umpires_enabled ? (
            <>
              <Panel className="col-span-2" title="Live Courts" badge={live.length ? `${live.length} on court` : null} testid="cw-tv-live">
                {live.length === 0 ? (
                  <Empty>No match in progress. Press <b>Start Match</b> on an umpire console to feature it here.</Empty>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <AnimatePresence initial={false}>
                      {live.map((lm) => <LiveCard key={lm._key} lm={lm} />)}
                    </AnimatePresence>
                  </div>
                )}
              </Panel>
              <div className="flex flex-col gap-4 min-h-0">
                <Panel title="Standings" testid="cw-tv-standings" className="flex-1">
                  <CwStandings rows={d.standings} variant="compact" />
                </Panel>
                <Panel title="Results & Up Next" testid="cw-tv-queue" className="flex-1">
                  <ResultList finished={finished} />
                  <UpcomingList upcoming={upcoming} />
                </Panel>
              </div>
            </>
          ) : (
            <>
              <Panel className="col-span-2" title="Standings" testid="cw-tv-standings">
                <CwStandings rows={d.standings} variant="tv" />
              </Panel>
              <div className="flex flex-col gap-4 min-h-0">
                <Panel title="Recent Results" testid="cw-tv-results" className="flex-1">
                  <ResultList finished={finished} />
                  {finished.length === 0 && <Empty>No finished matches yet.</Empty>}
                </Panel>
                <Panel title="Up Next" testid="cw-tv-upnext" className="flex-1">
                  <UpcomingList upcoming={upcoming} />
                  {upcoming.length === 0 && <Empty>Nothing scheduled.</Empty>}
                </Panel>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Panel({ title, badge, children, className = "", testid }) {
  return (
    <div className={`rounded-2xl glow-border overflow-hidden flex flex-col min-h-0 ${className}`} style={{ background: "var(--surface)" }} data-testid={testid}>
      <div className="px-4 py-2.5 font-display font-bold flex items-center gap-2 shrink-0 border-b" style={{ borderColor: "var(--border)" }}>
        <Trophy className="w-4 h-4 text-primary-c" /> {title}
        {badge && <span className="ml-auto text-[10px] mono uppercase tracking-widest rounded-full px-2 py-0.5 glass text-primary-c">{badge}</span>}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto">{children}</div>
    </div>
  );
}

function LiveCard({ lm }) {
  const fx = lm._fx;
  const lastSet = (lm.sets || [])[(lm.sets || []).length - 1];
  const aScore = lm.live_a != null ? lm.live_a : (lastSet ? lastSet.a : 0);
  const bScore = lm.live_b != null ? lm.live_b : (lastSet ? lastSet.b : 0);
  const dot = (side) => (lm.serving_team === side) ? <span className="inline-block w-2.5 h-2.5 rounded-full ml-2 animate-pulse" style={{ background: "#22c55e", boxShadow: "0 0 10px #22c55e" }} data-testid={`cw-serve-dot-${side}`} /> : null;
  return (
    <motion.div layout initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
      className="rounded-2xl glow-border p-4" style={{ background: "var(--bg)" }} data-testid="cw-tv-line-card">
      <div className="flex items-center justify-between mb-2 text-[10px] mono uppercase tracking-widest" style={{ color: "var(--text-2)" }}>
        <span>{categoryLabel(lm)}</span>
        <span className="flex items-center gap-2">{lm.court ? `Court ${lm.court}` : ""}<span className="rounded-full px-2 py-0.5 glass text-primary-c">live</span></span>
      </div>
      <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 sm:gap-3">
        <div className="text-right min-w-0">
          <div className="font-display font-black text-base sm:text-lg leading-tight truncate">{(lm.a_player_names || []).join(" / ") || fx.team_a_name}{dot("a")}</div>
          <div className="text-[10px] mono uppercase truncate" style={{ color: "var(--text-2)" }}>{fx.team_a_name}</div>
        </div>
        <div className="font-display font-black text-3xl sm:text-5xl tabular-nums text-center flex items-baseline justify-center shrink-0">
          <Pulse v={aScore} /><span style={{ color: "var(--text-2)" }} className="mx-1">–</span><Pulse v={bScore} />
        </div>
        <div className="text-left min-w-0">
          <div className="font-display font-black text-base sm:text-lg leading-tight truncate">{dot("b")}{(lm.b_player_names || []).join(" / ") || fx.team_b_name}</div>
          <div className="text-[10px] mono uppercase truncate" style={{ color: "var(--text-2)" }}>{fx.team_b_name}</div>
        </div>
      </div>
      {(lm.sets || []).length > 0 && <div className="text-center text-xs mono mt-2" style={{ color: "var(--text-2)" }}>{lm.sets.map((s) => `${s.a}-${s.b}`).join(", ")}</div>}
    </motion.div>
  );
}

function Pulse({ v }) {
  return <motion.span key={v} initial={{ scale: 1.5, color: "var(--primary-ink)" }} animate={{ scale: 1, color: "var(--text)" }} transition={{ duration: 0.5 }} className="inline-block">{v}</motion.span>;
}

function ResultList({ finished }) {
  return (
    <AnimatePresence initial={false}>
      {finished.map((lm) => {
        const fx = lm._fx;
        const winA = lm.winner === "a";
        const winNames = (winA ? lm.a_player_names : lm.b_player_names) || [];
        const winTeam = winA ? fx.team_a_name : fx.team_b_name;
        return (
          <motion.div key={lm._key} layout initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}
            className="px-3 py-2.5 border-b text-sm" style={{ borderColor: "var(--border)" }} data-testid="cw-tv-result">
            <div className="flex items-center justify-between text-[10px] mono uppercase tracking-wider mb-1" style={{ color: "var(--text-2)" }}>
              <span>{categoryLabel(lm)}</span>
              <span className="mono">{(lm.sets || []).map((s) => `${s.a}-${s.b}`).join(", ")}</span>
            </div>
            <div className="flex items-center gap-1.5 font-bold">
              <Crown className="w-3.5 h-3.5 shrink-0" style={{ color: "#eab308" }} />
              <span className="truncate">{winNames.join(" / ") || winTeam}</span>
              <span className="text-[10px] mono font-normal ml-auto shrink-0" style={{ color: "var(--text-2)" }}>{winTeam}</span>
            </div>
          </motion.div>
        );
      })}
    </AnimatePresence>
  );
}

function UpcomingList({ upcoming }) {
  return (
    <AnimatePresence initial={false}>
      {upcoming.map((lm) => {
        const fx = lm._fx;
        const a = (lm.a_player_names || []).join(" / ") || fx.team_a_name;
        const b = (lm.b_player_names || []).join(" / ") || fx.team_b_name;
        return (
          <motion.div key={lm._key} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="px-3 py-2 border-b text-sm flex items-center gap-2" style={{ borderColor: "var(--border)" }} data-testid="cw-tv-upcoming">
            <span className="text-[10px] mono uppercase shrink-0 rounded px-1.5 py-0.5" style={{ background: "var(--bg)", color: "var(--text-2)" }}>{lm.court ? `Ct${lm.court}` : "—"}</span>
            <span className="truncate flex-1">{a} <span style={{ color: "var(--text-2)" }}>vs</span> {b}</span>
            <span className="text-[10px] mono shrink-0" style={{ color: "var(--text-2)" }}>{categoryLabel(lm)}</span>
          </motion.div>
        );
      })}
    </AnimatePresence>
  );
}

function Empty({ children }) {
  return <div className="px-4 py-8 text-center text-sm" style={{ color: "var(--text-2)" }}>{children}</div>;
}

function Center({ children }) {
  return <div className="min-h-screen flex items-center justify-center text-sm" style={{ background: "var(--bg)", color: "var(--text-2)" }}>{children}</div>;
}
